import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { NumericFormat } from "react-number-format";
import ModalComponent from "..";
import {
  useCreateSalesAgentMutation,
  useUpdateSalesAgentMutation,
} from "../../../redux/features/admin/sales/sales.api";
import { SingleSalesAgent } from "../../../redux/features/admin/sales/sales.types";
import { getCurrencySymbol, parseStringToNumber } from "../../../utils/helpers";
import { ModalCompProps } from "../../../utils/types";
import { ManageSalesAgentSchema } from "../../../utils/validations";
import { Button } from "../../buttons/button";
import { Icon } from "../../icon/icon";
import { TextInput } from "../../inputs/textInput";
import { MiniHeader } from "../../typography/miniHeader";

export const ManageSalesAgentModal = ({
  open,
  handleClose,
  onSuccess,
  agent,
}: ModalCompProps & {
  onSuccess: () => void;
  agent?: SingleSalesAgent;
}) => {
  // eslint-disable-next-line
  const [mode, setMode] = useState<"create" | "edit">("create");

  const [createSalesAgent, { isLoading: creatingAgent }] =
    useCreateSalesAgentMutation();

  const [editSalesAgent, { isLoading: editingSalesAgent }] =
    useUpdateSalesAgentMutation();

  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleBlur,
    handleChange,
    resetForm,
    isValid,
  } = useFormik({
    initialValues: {
      email: agent?.email ?? "",
      firstName: agent?.firstName ?? "",
      lastName: agent?.lastName ?? "",
      ngn: agent?.commission?.NGN ?? "",
      cad: agent?.commission?.CAD ?? "",
    },
    enableReinitialize: true,
    validationSchema: ManageSalesAgentSchema,
    onSubmit: async (vals) => {
      if (mode === "create") {
        const resp = await createSalesAgent({
          email: vals.email,
          firstName: vals.firstName,
          lastName: vals.lastName,
          commission: {
            NGN: parseStringToNumber(vals.ngn)!,
            CAD: parseStringToNumber(vals.cad)!,
          },
        });

        if (resp.data?.status === "success") {
          resetForm();
          onSuccess();
          handleClose();
        }
      } else {
        const resp = await editSalesAgent({
          id: agent?._id!,
          firstName: vals.firstName,
          lastName: vals.lastName,
          commission: {
            NGN: parseStringToNumber(vals.ngn)!,
            CAD: parseStringToNumber(vals.cad)!,
          },
        });

        if (resp.data?.status === "success") {
          resetForm();
          onSuccess();
          handleClose();
        }
      }
      return;
    },
  });

  useEffect(() => {
    if (open) {
      setMode(!!agent ? "edit" : "create");
    }
  }, [open, agent]);

  return (
    <ModalComponent
      open={open}
      handleClose={() => {
        handleClose();
        resetForm();
      }}
      className=""
      dialogClassName=""
      closeOnOverlayClick
      showHeader={false}
      title={undefined}
      subtitle={undefined}
      showBackIcon={false}
      mobileAnchorBottom
      showCloseIcon={false}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Icon
          svg={"user-plus"}
          width={42}
          height={42}
          className="rounded-xl border border-grey-100 p-[11px] text-grey-700"
        />

        <MiniHeader
          header={`${mode === "create" ? "Add" : "Update"} Sales Agent`}
          subheader={undefined}
        />

        <div className="grid grid-cols-2 gap-4">
          <TextInput
            type="text"
            value={values.firstName}
            hasTitle
            title="First Name"
            placeholder="Enter first name"
            onBlur={handleBlur}
            onChange={handleChange}
            name="firstName"
            readOnly={false}
            errors={
              touched.firstName && errors.firstName
                ? errors.firstName
                : undefined
            }
          />

          <TextInput
            type="text"
            value={values.lastName}
            hasTitle
            title="Last Name"
            placeholder="Enter last name"
            onBlur={handleBlur}
            onChange={handleChange}
            name="lastName"
            readOnly={false}
            errors={
              touched.lastName && errors.lastName ? errors.lastName : undefined
            }
          />

          <TextInput
            type="text"
            value={values.email}
            hasTitle
            title="Email"
            placeholder="Enter email"
            onBlur={handleBlur}
            onChange={handleChange}
            name="email"
            readOnly={false}
            errors={touched.email && errors.email ? errors.email : undefined}
            containerClassName="!col-span-full"
            disabled={mode === "edit"}
          />

          <div className="col-span-full flex flex-col gap-2">
            <p className="text-sm font-semibold text-grey-700">
              Ticket Commission Amount
            </p>

            <div className="grid grid-cols-2 gap-4">
              <NumericFormat
                suffix={undefined}
                prefix={getCurrencySymbol("NGN")}
                decimalScale={2}
                thousandSeparator
                allowLeadingZeros={false}
                customInput={TextInput}
                type="text"
                value={values.ngn}
                hasTitle
                title="NGN Commission Amount"
                placeholder="Enter NGN commission amount"
                onBlur={handleBlur}
                onChange={handleChange}
                name="ngn"
                readOnly={false}
                errors={touched.ngn && errors.ngn ? errors.ngn : undefined}
              />

              <NumericFormat
                suffix={undefined}
                prefix={getCurrencySymbol("CAD")}
                decimalScale={2}
                thousandSeparator
                allowLeadingZeros={false}
                customInput={TextInput}
                type="text"
                value={values.cad}
                hasTitle
                title="CAD Commission Amount"
                placeholder="Enter CAD commission amount"
                onBlur={handleBlur}
                onChange={handleChange}
                name="cad"
                readOnly={false}
                errors={touched.cad && errors.cad ? errors.cad : undefined}
              />
            </div>
          </div>
        </div>

        <div className="flex w-full items-center justify-end gap-2">
          <Button
            text={"Cancel"}
            type="button"
            onClick={async () => {
              handleClose();
              resetForm();
            }}
            variation={"plain-grey"}
            className="max-w-fit"
            loading={false}
            disabled={false}
          />

          <Button
            text={mode === "create" ? "Add Sales Agent" : "Save Changes"}
            type="submit"
            onClick={() => {}}
            className="max-w-fit"
            loading={creatingAgent || editingSalesAgent}
            disabled={!isValid || creatingAgent || editingSalesAgent}
            variation={"primary"}
          />
        </div>
      </form>
    </ModalComponent>
  );
};
